import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Text, TextInput, SelectInput, Button, DateInput, Icon } from '@/ui-kits';
import { DataTable } from '@/components';
import { DataTableColumn, SelectInputitem, FinancePayment, GetFinancePaymentsRequestPayload } from '@/interface';
import { useSelector, useDispatch } from 'react-redux';
import {
  financePaymentsSelector,
  financePaymentsCurrentPageSelector,
  financePaymentsTotalPagesSelector,
  financePaymentsTotalResultsSelector,
  financeAdminStatus,
  financeAdminFailureSelector,
  financeAdminSuccessSelector,
  financeAdminRefundLoadingSelector,
} from '@/services/redux/selectors';
import { getFinancePaymentsRequest, refundPaymentRequest, resetFinanceAdminMessage } from '@/services/redux/slices';
import { ErrorCodes, getErrorMessage, SuccessCodes } from '@/utils/getMessages';
import { useToast, useDebounceState } from '@/hooks';
import { formatDate } from '@/utils/common_functions';
import { LoaderCircle } from 'lucide-react';

const statusOptions: SelectInputitem[] = [
  { id: 1, label: 'Success' },
  { id: 2, label: 'Refund' },
];

export const PatientPayments: React.FC = () => {
  const { showToast } = useToast();
  const dispatch = useDispatch();

  // Selectors
  const payments = useSelector(financePaymentsSelector);
  const currentPageFromRedux = useSelector(financePaymentsCurrentPageSelector);
  const totalPages = useSelector(financePaymentsTotalPagesSelector);
  const totalResults = useSelector(financePaymentsTotalResultsSelector);
  const isLoading = useSelector(financeAdminStatus);
  const refundLoading = useSelector(financeAdminRefundLoadingSelector);
  const failure = useSelector(financeAdminFailureSelector) as ErrorCodes;
  const success = useSelector(financeAdminSuccessSelector) as SuccessCodes;

  // States
  const [search, setSearch, immediateSearch] = useDebounceState<string>('');
  const [selectedStatus, setSelectedStatus, immediateSelectedStatus] = useDebounceState<SelectInputitem | null>(null);
  const [fromDate, setFromDate, immediateFromDate] = useDebounceState<Date | null>(null);
  const [toDate, setToDate, immediateToDate] = useDebounceState<Date | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingRowId, setEditingRowId] = useState<number | null>(null); // Use case_id which is unique
  const [editedStatus, setEditedStatus] = useState<'Success' | 'Refund' | null>(null);
  const prevFiltersRef = useRef({ search, selectedStatus, fromDate, toDate });
  const [emptyScreen, setEmptyScreen] = useState(false);

  // Fetch payments
  const fetchPayments = useCallback(() => {
    const payload: GetFinancePaymentsRequestPayload = {
      page: currentPage,
    };
    if (search) {
      payload.search_text = search;
    }
    if (fromDate) {
      payload.start_date = fromDate.toISOString().split('T')[0];
    }
    if (toDate) {
      payload.end_date = toDate.toISOString().split('T')[0];
    }
    if (selectedStatus) {
      payload.status = selectedStatus.label.toLowerCase();
    }
    dispatch(getFinancePaymentsRequest(payload));
  }, [currentPage, search, fromDate, toDate, selectedStatus, dispatch]);



  // Handlers
  const handleEdit = (row: FinancePayment) => {
    // Always set the new editing row using case_id (unique identifier)
    // This will automatically close any previous edit mode
    setEditingRowId(row.case_id);
    // Map status: 'success' or 'Success' -> 'Success', 'Refund' or 'Refunded' -> 'Refund'
    const currentStatus = row.status.toLowerCase();
    if (currentStatus === 'success') {
      setEditedStatus('Success');
    } else if (currentStatus === 'refund' || currentStatus === 'refunded') {
      setEditedStatus('Refund');
    } else {
      setEditedStatus('Success');
    }
  };

  const handleSave = (caseId: number) => {
    if (editedStatus && editingRowId) {
      // Pass status as string directly
      dispatch(refundPaymentRequest({ id: caseId, status: editedStatus }));
    }
  };

  const handleCancel = () => {
    setEditingRowId(null);
    setEditedStatus(null);
  };

  const resetFilter = () => {
    setSearch('');
    setSelectedStatus(null);
    setFromDate(null);
    setToDate(null);
    setCurrentPage(1);
  };

  const isFilterActive = useMemo(() => {
    return !!search || !!selectedStatus || !!fromDate || !!toDate;
  }, [search, selectedStatus, fromDate, toDate]);

  // Transform API data for display
  const transformedPayments = useMemo(() => {
    return payments.map(payment => ({
      ...payment,
      formattedDate: formatDate(payment.payment_date, 'dd-MMM-yyyy, HH:mm:ss'),
      patientId: `${payment.patient.id}`,
      caseId: `${payment.case_id}`,
    }));
  }, [payments]);

  const columns: DataTableColumn<FinancePayment & { formattedDate: string; patientId: string; caseId: string }>[] = useMemo(() => {
    return [
      {
        name: 'transaction_id',
        title: <Text>TRANSACTION ID</Text>,
        render: row => row.transaction_id,
        headerAlign: 'left',
        width: { minWidth: '200px', maxWidth: '250px' },
        align: 'left',
      },
      {
        name: 'caseId',
        title: <Text>CASE ID</Text>,
        render: row => row.caseId,
        headerAlign: 'center',
        width: { minWidth: '120px', maxWidth: '150px' },
        align: 'center',
      },
      {
        name: 'patientId',
        title: <Text>PATIENT ID</Text>,
        render: row => row.patientId,
        headerAlign: 'center',
        width: { minWidth: '150px', maxWidth: '200px' },
        align: 'center',
      },
      {
        name: 'patientName',
        title: <Text>PATIENT NAME</Text>,
        render: row => (
          <div className="whitespace-normal break-words">{row.patient.name}</div>
        ),
        headerAlign: 'center',
        width: { minWidth: '200px', maxWidth: '250px' },
        align: 'center',
      },
      {
        name: 'amount',
        title: <Text>AMOUNT</Text>,
        render: row => `₹${row.amount.toLocaleString()}`,
        headerAlign: 'right',
        width: { minWidth: '100px', maxWidth: '150px' },
        align: 'center',
      },
      {
        name: 'status',
        title: <Text>STATUS</Text>,
        render: row => {
          const isEditing = editingRowId === row.case_id;
          if (isEditing) {
            const currentValue = editedStatus === 'Success' ? 1 : editedStatus === 'Refund' ? 2 : 1;
            return (
              <SelectInput
                value={currentValue}
                onChange={item => {
                  const newStatus = item.label as 'Success' | 'Refund';
                  setEditedStatus(newStatus);
                }}
                options={statusOptions}
                wrapperClassName="border !py-0.5 ml-4"
                className="min-w-[120px]"
                textClassName="!text-sm !py-1"
                itemTextClassName="!text-sm !font-FigtreeRegular"
                disabled={refundLoading}
              />
            );
          }
          return row.status;
        },
        headerAlign: 'center',
        width: { minWidth: '150px', maxWidth: '200px' },
        align: 'center',
      },
      {
        name: 'payment_date',
        title: <Text>PAYMENT DATE & TIME</Text>,
        render: row => row.formattedDate,
        headerAlign: 'center',
        width: { minWidth: '250px', maxWidth: '300px' },
        align: 'center',
      },
      // {
      //   name: 'actions',
      //   title: <Text>ACTIONS</Text>,
      //   render: row => {
      //     const isEditing = editingRowId === row.case_id;
      //     if (isEditing) {
      //       return (
      //         <div className="flex justify-center gap-4">
      //           <button
      //             onClick={() => handleSave(row.case_id)}
      //             className="flex justify-center"
      //             disabled={refundLoading}
      //           >
      //             <Icon type="green_tick_btn" className="size-5" />
      //           </button>
      //           <button
      //             onClick={handleCancel}
      //             className="flex justify-center"
      //             disabled={refundLoading}
      //           >
      //             <Icon type="cross_btn_red" className="size-5" />
      //           </button>
      //         </div>
      //       );
      //     }
      //     return (
      //       <div className="flex justify-center">
      //         <button
      //           onClick={() => handleEdit(row)}
      //           className="flex justify-center"
      //           disabled={refundLoading}
      //         >
      //           <Icon type="edit_input" className="size-4" />
      //         </button>
      //       </div>
      //     );
      //   },
      //   headerAlign: 'center',
      //   width: { minWidth: '100px', maxWidth: '120px' },
      //   align: 'center',
      // },
    ];
  }, [editingRowId, editedStatus, refundLoading]);

  // Reset page when filters change
  useEffect(() => {
    const prevFilters = prevFiltersRef.current;
    const filtersChanged =
      prevFilters.search !== search ||
      prevFilters.selectedStatus?.id !== selectedStatus?.id ||
      prevFilters.fromDate?.getTime() !== fromDate?.getTime() ||
      prevFilters.toDate?.getTime() !== toDate?.getTime();

    if (filtersChanged) {
      setCurrentPage(1);
      prevFiltersRef.current = { search, selectedStatus, fromDate, toDate };
    }
  }, [search, selectedStatus, fromDate, toDate]);

  // Side effects
  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  useEffect(() => {
    if (failure) {
      if (['E-10044', 'E-10054'].includes(failure)) {

        // if failure is E-10054, set empty screen to true
        if (failure === 'E-10054') {
          setEmptyScreen(true);
        }
        return;
      }
      showToast(getErrorMessage(failure), 'failure');
    }
    return () => {
      dispatch(resetFinanceAdminMessage());
    };
  }, [failure, showToast, dispatch]);

  useEffect(() => {
    if (success && String(success) === 'S-10102') {
      setEditingRowId(null);
      setEditedStatus(null);
      // Refetch payments after successful refund
      fetchPayments();
    }
    return () => {
      dispatch(resetFinanceAdminMessage());
    };
  }, [success, showToast, dispatch, fetchPayments]);


  if (isLoading && !isFilterActive) {
    return (
      <div className="flex items-center justify-center px-4 sm:px-6 py-4 sm:py-6">
        <span className="text-ui_grey">Loading...</span>
        <LoaderCircle className="size-4 animate-spin text-ui_grey" />
      </div>
    );
  }

  // Empty state
  if (!isLoading && emptyScreen) {
    return (
      <div className="flex flex-col grow p-2 sm:p-4 md:p-6 lg:p-8">
        <Text variant="subheading" className="!font-FigtreeMedium text-deep_blue">
          Track and manage all patient payments
        </Text>

        <div className="flex flex-col items-center justify-center flex-1 py-12">
          <Text variant="heading" className="text-deep_blue mb-2 !font-FigtreeBold">
            No payments yet
          </Text>
          <Text variant="body" className="text-deep_blue text-center !text-lg !font-FigtreeRegular">
            Payments made by patients will appear here once a consultation is booked.
          </Text>
        </div>
      </div>
    );
  }

  // Table view with data
  return (
    <div className="flex flex-col grow p-2 sm:p-4 md:p-6 lg:p-8">
      <Text variant="subheading" className="!font-FigtreeMedium text-deep_blue mb-2 overflow-hidden text-ellipsis !text-xl sm:!text-2xl">
        Track and manage all patient payments
      </Text>

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 my-4 mb-6 flex-wrap">
        <TextInput
          value={immediateSearch}
          onChangeText={setSearch}
          wrapperClassName="border pl-2"
          className="w-full sm:flex-1 sm:min-w-[450px] sm:max-w-[450px]"
          leftIcon="search_blue"
          name="search"
          placeholder="Search by Name, Transaction ID, Case ID or Patient ID"
        />
        <div className="flex flex-row gap-4 w-full sm:flex-none sm:contents">
          <DateInput
            value={immediateFromDate}
            onChangeDate={setFromDate}
            placeholder="From"
            wrapperClassName="border rounded-lg"
            className="flex-[1.1] w-full sm:flex-1 sm:min-w-[120px] sm:max-w-[150px]"
            textClassName="text-start text-ui_blue whitespace-nowrap overflow-hidden text-ellipsis"
            maxValue={immediateToDate ?? new Date()}
          />
          <DateInput
            value={immediateToDate}
            onChangeDate={setToDate}
            placeholder="To"
            wrapperClassName="border rounded-lg"
            className="flex-1 w-full sm:flex-1 sm:min-w-[120px] sm:max-w-[150px]"
            textClassName="text-start text-ui_blue whitespace-nowrap overflow-hidden text-ellipsis"
            minValue={fromDate ?? undefined}
            maxValue={new Date()}
          />
        </div>
        <div className="flex flex-row gap-4 w-full sm:flex-none sm:contents">
          <SelectInput
            wrapperClassName="border"
            name="status"
            className="w-1/2 sm:flex-1 sm:min-w-[100px] sm:max-w-[150px]"
            placeholder="Status"
            options={statusOptions}
            value={immediateSelectedStatus?.id || ''}
            onChange={setSelectedStatus}
          />
          {isFilterActive && (
            <Button
              title="Reset Filters"
              variant="secondary"
              onClick={resetFilter}
              className="w-auto sm:w-fit sm:flex-none self-start sm:self-auto flex-shrink-0 text-xs sm:text-base px-2 sm:px-4"
            />
          )}
        </div>
      </div>

      <DataTable
        data={transformedPayments}
        columns={columns}
        totalPages={totalPages}
        currentPage={currentPageFromRedux || currentPage}
        totalResult={totalResults}
        onPageChange={page => {
          setCurrentPage(page);
        }}
        pageSize={10}
        loading={isLoading}
      />
    </div>
  );
};
